import React, { useState } from 'react';
import { PlusCircle, Loader2, Check, X, Trash2 } from 'lucide-react';
import NumberInput from './NumberInput';
import { parseDecimal } from '../utils/utils';
import {
    RettificaEstratto, VoceEstratto, VOCI_ESTRATTO, VOCE_ESTRATTO_LABEL, VOCE_ESTRATTO_UNITA,
    sommaRettificheEstratto, formatQuantitaEstratto,
} from '../utils/rettificheEstratto';

interface Props {
    initial: RettificaEstratto[];
    // Quantità dai report approvati, per mostrare il risultato e impedire voci sotto zero.
    daReport: Record<VoceEstratto, number>;
    saving: boolean;
    onClose: () => void;
    onSave: (list: RettificaEstratto[]) => void;
}

const RettificheEstrattoModal: React.FC<Props> = ({ initial, daReport, saving, onClose, onSave }) => {
    const [edit, setEdit] = useState<RettificaEstratto[]>(initial.map(r => ({ ...r })));

    const somme = sommaRettificheEstratto(edit);
    const sottoZero = VOCI_ESTRATTO.filter(v => daReport[v] + somme[v] < -0.0001);
    const vuote = edit.some(r => !(Math.abs(r.quantita) > 0));

    const upd = (i: number, patch: Partial<RettificaEstratto>) => {
        const next = [...edit];
        next[i] = { ...next[i], ...patch };
        setEdit(next);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
             onClick={() => !saving && onClose()}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden animate-in slide-in-from-bottom-4"
                 onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div>
                        <h3 className="text-lg font-black text-slate-900">Rettifiche Estratto Conto</h3>
                        <p className="text-xs text-slate-500 mt-1">
                            Correggono ore, km e spese solo verso il cliente. I report e i compensi dei collaboratori non cambiano.
                        </p>
                    </div>
                    <button onClick={onClose} disabled={saving}
                            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
                        <X size={18}/>
                    </button>
                </div>

                <div className="p-5 space-y-2.5 max-h-[45vh] overflow-y-auto">
                    {edit.length === 0 && (
                        <div className="text-center py-6 text-slate-400 text-sm">Nessuna rettifica. Aggiungila con il pulsante in basso.</div>
                    )}
                    {edit.map((r, i) => {
                        // -0 = "in meno" ancora senza quantità.
                        const neg = r.quantita < 0 || Object.is(r.quantita, -0);
                        return (
                            <div key={r.id} className="space-y-1.5 border-b border-slate-50 pb-2.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <select
                                        value={r.voce}
                                        onChange={e => upd(i, { voce: e.target.value as VoceEstratto })}
                                        className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-blue-300 bg-white"
                                    >
                                        {VOCI_ESTRATTO.map(v => <option key={v} value={v}>{VOCE_ESTRATTO_LABEL[v]}</option>)}
                                    </select>
                                    <button
                                        onClick={() => upd(i, { quantita: -r.quantita })}
                                        className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-black border transition-colors ${
                                            neg ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                                                : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'}`}
                                        title="Inverti in più / in meno"
                                    >
                                        {neg ? '− In meno' : '+ In più'}
                                    </button>
                                    <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden shrink-0">
                                        <NumberInput
                                            min="0"
                                            step="0.01"
                                            value={Math.abs(r.quantita) || ''}
                                            onChange={e => {
                                                // Il segno resta quello scelto col pulsante.
                                                const v = parseDecimal(e.target.value);
                                                upd(i, { quantita: neg ? -v : v });
                                            }}
                                            className="w-20 px-2 py-1.5 text-sm outline-none"
                                            placeholder="0"
                                        />
                                        <span className="px-2 text-xs text-slate-400 bg-slate-50 border-l border-slate-200 py-1.5">{VOCE_ESTRATTO_UNITA[r.voce]}</span>
                                    </div>
                                    <button
                                        onClick={() => setEdit(edit.filter((_, j) => j !== i))}
                                        className="shrink-0 p-1.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors ml-auto"
                                    >
                                        <X size={14}/>
                                    </button>
                                </div>
                                <input
                                    type="text"
                                    value={r.nota || ''}
                                    onChange={e => upd(i, { nota: e.target.value })}
                                    placeholder="Nota interna (facoltativa)"
                                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-blue-300"
                                />
                            </div>
                        );
                    })}
                </div>

                <div className="px-5 pb-3">
                    <button
                        onClick={() => setEdit([...edit, { id: `${Date.now()}-${Math.random()}`, voce: 'KM', quantita: -0 }])}
                        className="w-full border-2 border-dashed border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl py-2 text-xs font-bold flex items-center justify-center gap-1 transition-colors"
                    >
                        <PlusCircle size={13}/> Aggiungi rettifica
                    </button>
                </div>

                {edit.length > 0 && (
                    <div className="mx-5 mb-3 p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                        {VOCI_ESTRATTO.filter(v => Math.abs(somme[v]) > 0).map(v => {
                            const tot = daReport[v] + somme[v];
                            return (
                                <div key={v} className="flex items-center justify-between gap-2 text-xs">
                                    <span className="text-slate-500">{VOCE_ESTRATTO_LABEL[v]}</span>
                                    <span className={`font-bold ${tot < -0.0001 ? 'text-rose-600' : 'text-slate-800'}`}>
                                        {formatQuantitaEstratto(v, daReport[v], false)} {formatQuantitaEstratto(v, somme[v])} = {formatQuantitaEstratto(v, tot, false)}
                                    </span>
                                </div>
                            );
                        })}
                        {sottoZero.length > 0 && (
                            <div className="text-[11px] font-semibold text-rose-600 pt-1">
                                Una voce non può scendere sotto zero: correggi {sottoZero.map(v => VOCE_ESTRATTO_LABEL[v].toLowerCase()).join(', ')}.
                            </div>
                        )}
                    </div>
                )}

                <div className="p-5 pt-2 border-t border-slate-100 flex items-center justify-between gap-3">
                    {edit.length > 0 ? (
                        <button onClick={() => setEdit([])} disabled={saving}
                                className="text-xs text-slate-400 hover:text-rose-500 flex items-center gap-1 disabled:opacity-50">
                            <Trash2 size={12}/> Rimuovi tutte
                        </button>
                    ) : <div/>}
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} disabled={saving}
                                className="px-4 py-2 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl disabled:opacity-50">
                            Annulla
                        </button>
                        <button
                            onClick={() => onSave(edit.map(r => ({ ...r, nota: r.nota?.trim() || undefined })))}
                            disabled={saving || sottoZero.length > 0 || vuote}
                            title={vuote ? 'Inserisci la quantità di ogni rettifica' : undefined}
                            className="px-5 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
                        >
                            {saving ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
                            Salva rettifiche
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RettificheEstrattoModal;
